import React, { useState, useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import './WaveForecastDashboard.css';

const WaveForecastDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [horizonYears, setHorizonYears] = useState(5);

  useEffect(() => {
    fetchForecastData();
  }, [horizonYears]);

  const fetchForecastData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosPrivate.get(`/WaveForecasting/predictFutureWaves?horizonYears=${horizonYears}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load forecast data');
      console.error('Forecast error:', err);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="wave-forecast-container">
        <div className="loading-spinner">Loading forecast data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wave-forecast-container">
        <div className="error-message">
          <h3>Error Loading Forecast</h3>
          <p>{error}</p>
          <button onClick={fetchForecastData}>Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="wave-forecast-container">
      <header className="forecast-header">
        <h1>📈 M&A Wave Forecasting Dashboard</h1>
        <p className="subtitle">
          Time-Series Analysis Using Holt-Winters Exponential Smoothing
        </p>
      </header>

      {}
      <section className="dataset-info-section">
        <h2>Dataset Information</h2>
        <div className="info-grid">
          <div className="info-card">
            <div className="info-label">Events Analyzed</div>
            <div className="info-value">{data.datasetInfo.totalEventsAnalyzed}</div>
          </div>
          <div className="info-card">
            <div className="info-label">Paper Reference Size</div>
            <div className="info-value">{data.datasetInfo.paperReferenceSize}</div>
          </div>
          <div className="info-card">
            <div className="info-label">Date Range</div>
            <div className="info-value">
              {data.datasetInfo.dateRange.earliest} → {data.datasetInfo.dateRange.latest}
            </div>
          </div>
          <div className="info-card">
            <div className="info-label">Span (Years)</div>
            <div className="info-value">{data.datasetInfo.dateRange.spanYears.toFixed(1)}</div>
          </div>
        </div>
      </section>

      {}
      <section className="method-section">
        <h2>Forecasting Method</h2>
        <div className="method-card">
          <div className="method-name">{data.forecast.method}</div>
          <div className="method-details">
            <span><strong>Forecast Date:</strong> {data.forecast.forecastDate}</span>
            <span><strong>Horizon:</strong> {data.forecast.horizonYears} years</span>
            <span><strong>Confidence Level:</strong> {data.forecast.confidenceLevel}</span>
          </div>
        </div>

        {}
        <div className="horizon-selector">
          <label>Forecast Horizon (Years):</label>
          <select value={horizonYears} onChange={(e) => setHorizonYears(parseInt(e.target.value))}>
            <option value={3}>3 years</option>
            <option value={5}>5 years</option>
            <option value={7}>7 years</option>
            <option value={10}>10 years</option>
          </select>
        </div>
      </section>

      {}
      <section className="historical-waves-section">
        <h2>Historical M&A Waves Detected</h2>
        <p className="section-description">
          Peak detection using density-based analysis (threshold: μ + 1σ above mean activity)
        </p>
        <div className="waves-table-container">
          <table className="waves-table">
            <thead>
              <tr>
                <th>Wave ID</th>
                <th>Year</th>
                <th>Quarter</th>
                <th>Peak Date</th>
                <th>Intensity</th>
                <th>Transactions</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {data.historicalWaves.map((wave) => (
                <tr key={wave.waveId}>
                  <td>{wave.waveId}</td>
                  <td className="year-cell">{wave.year}</td>
                  <td>{wave.quarter}</td>
                  <td>{wave.peakDate}</td>
                  <td className="intensity-cell">{wave.intensity}</td>
                  <td>{wave.transactionCount}</td>
                  <td className="description-cell">{wave.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {}
      {data.futureWaves && data.futureWaves.length > 0 && (
        <section className="future-waves-section">
          <h2>SSA Extrapolation (Exploratory Only)</h2>
          <div className="warning-banner" style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px'
          }}>
            <strong>⚠️ Research Note:</strong> Forward prediction achieved 47.5% holdout accuracy,
            indicating EDA M&A is <strong>event-driven</strong> (strategic responses to technology disruptions)
            rather than cyclically predictable. SSA is effective for historical wave detection (30 peaks identified)
            but not for forecasting. Extrapolations shown below are for methodological completeness only.
          </div>
          <div className="predictions-grid">
            {data.futureWaves.map((wave) => (
              <div key={wave.waveNumber} className="prediction-card" style={{opacity: 0.6}}>
                <div className="prediction-header">
                  <div className="wave-number">Wave {wave.waveNumber}</div>
                  <div className="confidence-badge" style={{backgroundColor: '#9e9e9e'}}>
                    Low Confidence
                  </div>
                </div>
                <div className="prediction-body">
                  <div className="prediction-detail">
                    <span className="detail-label">Extrapolated Date:</span>
                    <span className="detail-value">{wave.predictedYear} {wave.predictedQuarter}</span>
                  </div>
                  <div className="prediction-detail">
                    <span className="detail-label">Linear Projection:</span>
                    <span className="detail-value">{wave.expectedIntensity} transactions</span>
                  </div>
                </div>
              </div>
          ))}
          </div>
        </section>
      )}

      {}
      <section className="validation-section">
        <h2>Validation Metrics</h2>
        <div className="validation-grid">
          <div className="validation-card">
            <div className="validation-label">Holdout Accuracy</div>
            <div className="validation-value">
              {data.validation?.holdoutAccuracy != null ? `${parseFloat(data.validation.holdoutAccuracy).toFixed(1)}%` : 'N/A'}
            </div>
            <div className="validation-interpretation">
              {data.validation?.holdoutAccuracy != null ? (
                parseFloat(data.validation.holdoutAccuracy) > 75 ? 'Excellent' :
                parseFloat(data.validation.holdoutAccuracy) > 60 ? 'Good' : 'Moderate'
              ) : 'Pending'}
            </div>
          </div>
          <div className="validation-card">
            <div className="validation-label">MAPE (Mean Absolute % Error)</div>
            <div className="validation-value">
              {data.validation?.MAPE != null ? `${parseFloat(data.validation.MAPE).toFixed(1)}%` : 'N/A'}
            </div>
            <div className="validation-interpretation">
              {data.validation?.MAPE != null ? (
                parseFloat(data.validation.MAPE) < 15 ? 'Excellent' :
                parseFloat(data.validation.MAPE) < 25 ? 'Good' : 'Moderate'
              ) : 'Pending'}
            </div>
          </div>
          <div className="validation-card">
            <div className="validation-label">RMSE (Root Mean Squared Error)</div>
            <div className="validation-value">
              {data.validation?.RMSE != null ? parseFloat(data.validation.RMSE).toFixed(2) : 'N/A'}
            </div>
            <div className="validation-interpretation">Lower is better</div>
          </div>
        </div>
        {data.validation?.note && (
          <div className="validation-note">
            <strong>Note:</strong> {data.validation.note}
          </div>
        )}
      </section>

      {}
      <section className="academic-summary-section">
        <h2>Academic Summary</h2>
        <div className="summary-box">
          <pre>{data.academicSummary}</pre>
        </div>
      </section>

      {}
      <div className="actions-section">
        <button className="refresh-button" onClick={fetchForecastData}>
          🔄 Refresh Forecast
        </button>
      </div>
    </div>
  );
};

export default WaveForecastDashboard;
